// src/pages/Deck/DeckBuilder.jsx

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getDeck, createDeck, updateDeck, getCardStatus } from '../../api/deckService';
import { getUserCollection, getAllCards } from '../../api/cardService';
import CardModal from '../../components/cards/CardModal';
import '../Collection/MyCollection.css';
import './DeckBuilder.css';

// Icônes d'action sur carte (oeil = détails, + = ajouter au deck, - = retirer du deck)
const EyeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
  </svg>
);
const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);
const MinusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);

const DeckBuilder = () => {
  const { deckId } = useParams();
  const navigate = useNavigate();

  const [availableCards, setAvailableCards] = useState([]);
  const [filteredCards, setFilteredCards] = useState([]);
  const [deckCards, setDeckCards] = useState([]); // liste avec doublons (exemplaires)
  const [deckInfo, setDeckInfo] = useState({
    name: '',
    element: 'FIRE',
    description: '',
    strategy: '',
    difficulty: 'Débutant'
  });
  // R41 : mode du deck — 'normal' (casual/PvE) | 'ranked' (classé).
  // En classé, les cartes en escrow marketplace (listées en vente) ou
  // verrouillées saisonnièrement par un autre compte sont interdites :
  // halo rouge + badge, ajout bloqué, sauvegarde refusée côté serveur.
  const [deckMode, setDeckMode] = useState('normal');
  const [cardStatus, setCardStatus] = useState({}); // { [cardId]: { listed, lockedByOther } }

  const [activeTab, setActiveTab] = useState('search');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);

  // Filtres / tri / affichage : IDENTIQUES à MyCollection ("Mes cartes")
  const [filters, setFilters] = useState({
    element: 'all',
    type: 'all',
    rarity: 'all',
    effect: 'all',
    search: ''
  });
  const [displayMode, setDisplayMode] = useState('list'); // 'list' | 'grid'
  const [gridColumns, setGridColumns] = useState(5);
  const [sortBy, setSortBy] = useState('default');

  // Charger les données nécessaires au montage
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        // Cartes : collection de l'utilisateur (même source que "Mes cartes"),
        // fallback sur toutes les cartes
        let cards = [];
        try {
          const userCollection = await getUserCollection();
          if (userCollection && userCollection.cards && userCollection.cards.length > 0) {
            cards = userCollection.cards;
          } else {
            const allCardsResponse = await getAllCards();
            cards = allCardsResponse.cards;
          }
        } catch (err) {
          const allCardsResponse = await getAllCards({ limit: 1000 });
          cards = allCardsResponse.cards;
        }
        setAvailableCards(cards);

        // R41 : statuts classé (escrow marketplace + verrou saisonnier) pour
        // toutes les cartes visibles — silencieux si l'API est indisponible.
        try {
          const st = await getCardStatus(cards.map((c) => c.id));
          if (st && st.cards) setCardStatus(st.cards);
        } catch (statErr) {
          console.warn('Statut marketplace indisponible :', statErr.message);
        }

        // Si deckId est fourni, charger les données du deck existant
        if (deckId) {
          const deckResponse = await getDeck(deckId);
          const deck = deckResponse.deck;
          if (deck.mode === 'ranked' || deck.mode === 'normal') setDeckMode(deck.mode);

          setDeckInfo({
            name: deck.name,
            element: deck.element,
            description: deck.description || '',
            strategy: deck.strategy || '',
            difficulty: deck.difficulty || 'Débutant'
          });

          // Trouver et ajouter les cartes du deck (doublons = exemplaires)
          const deckCardObjects = deck.cards.map(cardId => {
            return cards.find(card => card.id === cardId);
          }).filter(Boolean);

          setDeckCards(deckCardObjects);
        }

        setError(null);
      } catch (err) {
        setError(`Erreur lors du chargement des données: ${err.message}`);
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [deckId]);

  // Cartes sources selon l'onglet actif (recherche = collection, deck = contenu du deck)
  const sourceCards = activeTab === 'deck'
    ? Object.values(deckCards.reduce((acc, c) => {
        if (!acc[c.id]) acc[c.id] = { ...c, deckCount: 0 };
        acc[c.id].deckCount += 1;
        return acc;
      }, {}))
    : availableCards;

  // Filtrer les cartes : IDENTIQUE à MyCollection
  useEffect(() => {
    if (sourceCards.length === 0 && activeTab === 'deck') {
      setFilteredCards([]);
      return;
    }

    let result = [...sourceCards];

    if (filters.element !== 'all') {
      result = result.filter(card => card.element === filters.element);
    }

    if (filters.type !== 'all') {
      result = result.filter(card => card.type === filters.type);
    }

    if (filters.rarity !== 'all') {
      result = result.filter(card => card.rarity === filters.rarity);
    }

    if (filters.effect && filters.effect !== 'all') {
      const hasEffect = (c) => (Array.isArray(c.effects) && c.effects.length > 0) || !!c.effect || (Array.isArray(c.keywords) && c.keywords.length > 0) || (c.type === 'EQUIPMENT' && ((c.attackBonus || 0) > 0 || (c.defenseBonus || 0) > 0));
      result = result.filter(card => filters.effect === 'with' ? hasEffect(card) : !hasEffect(card));
    }

    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      result = result.filter(card =>
        card.name.toLowerCase().includes(searchTerm) ||
        (card.effect && card.effect.toLowerCase().includes(searchTerm))
      );
    }

    setFilteredCards(result);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, activeTab, availableCards, deckCards]);

  // Trier les cartes : IDENTIQUE à MyCollection
  const sortCards = (list) => {
    const sorted = [...list];
    const rarityRank = { RAINBOW: 5, LEGENDARY: 4, EPIC: 3, SUPER_RARE: 3, SUPER_SUPER_RARE: 4, RARE: 2, COMMON: 1, NORMAL: 1 };
    switch (sortBy) {
      case 'cost-asc': sorted.sort((a, b) => (a.cost ?? 99) - (b.cost ?? 99)); break;
      case 'cost-desc': sorted.sort((a, b) => (b.cost ?? -1) - (a.cost ?? -1)); break;
      case 'name-asc': sorted.sort((a, b) => a.name.localeCompare(b.name, 'fr')); break;
      case 'name-desc': sorted.sort((a, b) => b.name.localeCompare(a.name, 'fr')); break;
      case 'atk-desc': sorted.sort((a, b) => (b.attack ?? -1) - (a.attack ?? -1)); break;
      case 'def-desc': sorted.sort((a, b) => (b.defense ?? -1) - (a.defense ?? -1)); break;
      case 'rarity-desc': sorted.sort((a, b) => (rarityRank[b.rarity] ?? 0) - (rarityRank[a.rarity] ?? 0)); break;
      case 'acquisition':
        sorted.sort((a, b) =>
          new Date(b.acquiredAt || b.obtainedAt || b.createdAt || 0) -
          new Date(a.acquiredAt || a.obtainedAt || a.createdAt || 0));
        break;
      default: break;
    }
    return sorted;
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      [filterName]: value
    }));
  };

  // Mettre à jour les informations du deck
  const handleDeckInfoChange = (e) => {
    const { name, value } = e.target;
    setDeckInfo({
      ...deckInfo,
      [name]: value
    });
  };

  // Mettre à jour les modifications non sauvegardées
  useEffect(() => {
    if (!loading) {
      setUnsavedChanges(true);
    }
  }, [deckCards, deckInfo, loading]);

  // Avertir l'utilisateur avant de quitter s'il y a des modifications non sauvegardées
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (unsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [unsavedChanges]);

  // Nombre d'exemplaires d'une carte dans le deck
  const countInDeck = (cardId) => deckCards.filter(c => c.id === cardId).length;

  // R41 : raison de blocage en deck classé (null = autorisée)
  const rankedBlockReason = (card) => {
    if (deckMode !== 'ranked') return null;
    const st = cardStatus[card.id];
    if (!st) return null;
    if (st.listed) return 'Cette carte est listée en vente sur le marketplace (escrow) : elle ne peut pas être utilisée en classé. Annulez la vente pour la réutiliser.';
    if (st.lockedByOther) return 'Cette carte est déjà utilisée en classé par un autre compte cette saison. Elle redeviendra disponible à la prochaine saison.';
    return null;
  };

  // Ajouter une carte au deck (règles inchangées)
  const addCardToDeck = (card) => {
    const blockReason = rankedBlockReason(card);
    if (blockReason) {
      alert(blockReason);
      return;
    }

    if (card.element !== deckInfo.element && card.element !== 'RAINBOW') {
      alert(`Cette carte est de l'élément ${card.element} et ne peut pas être ajoutée à un deck ${deckInfo.element}.`);
      return;
    }

    if (deckCards.length >= 30) {
      alert('Le deck a atteint sa capacité maximale (30 cartes).');
      return;
    }

    const cardCount = countInDeck(card.id);
    // R54 : limite de copies par rareté — 3 (C/R), 2 (Épique), 1 (Légendaire)
    const copyLimit = ({ COMMON: 3, NORMAL: 3, RARE: 3, EPIC: 2, SUPER_RARE: 2, SUPER_SUPER_RARE: 1, LEGENDARY: 1 })[card.rarity] || 3;
    if (cardCount >= copyLimit) {
      alert('Limite de copies atteinte pour cette carte (max ' + copyLimit + ').');
      return;
    }

    setDeckCards([...deckCards, card]);
  };

  // Retirer un exemplaire d'une carte du deck
  const removeCardFromDeck = (cardId) => {
    const idx = deckCards.findIndex(c => c.id === cardId);
    if (idx === -1) return;
    const newDeckCards = [...deckCards];
    newDeckCards.splice(idx, 1);
    setDeckCards(newDeckCards);
  };

  // Sauvegarder le deck
  const saveDeck = async () => {
    if (!deckInfo.name.trim()) {
      alert('Veuillez entrer un nom pour votre deck.');
      return;
    }

    if (deckCards.length < 20) {
      alert('Votre deck doit contenir au moins 20 cartes.');
      return;
    }

    try {
      setSaving(true);

      const cardIds = deckCards.map(card => card.id);

      const deckData = {
        ...deckInfo,
        mode: deckMode,
        cards: cardIds
      };

      if (deckId) {
        await updateDeck(deckId, deckData);
      } else {
        await createDeck(deckData);
      }

      setUnsavedChanges(false);
      alert(`Deck ${deckId ? 'mis à jour' : 'créé'} avec succès !`);

      navigate('/collection?tab=decks');
    } catch (err) {
      // R41 : refus serveur (cartes escrow/verrouillées) → message exact
      const srvMsg = err && err.response && err.response.data && err.response.data.message;
      setError(srvMsg || `Erreur lors de la sauvegarde du deck: ${err.message}`);
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  // Annuler et revenir à la liste des decks
  const cancelEditing = () => {
    if (unsavedChanges) {
      const confirmLeave = window.confirm('Vous avez des modifications non sauvegardées. Êtes-vous sûr de vouloir quitter ?');
      if (!confirmLeave) return;
    }

    navigate('/collection?tab=decks');
  };

  // Compter les cartes par type
  const countCardsByType = (type) => {
    return deckCards.filter(card => card.type === type).length;
  };

  // Calculer le coût moyen en énergie
  const calculateAverageCost = () => {
    if (deckCards.length === 0) return 0;
    const totalCost = deckCards.reduce((sum, card) => sum + (card.cost || 0), 0);
    return (totalCost / deckCards.length).toFixed(1);
  };

  if (loading) {
    return (
      <div className="deck-builder-loading">
        <div className="loading-spinner"></div>
        <p>Chargement de l'éditeur de deck...</p>
      </div>
    );
  }

  // Actions overlay (oeil / + / -) affichées sur chaque carte
  const renderCardActions = (card) => {
    const inDeck = countInDeck(card.id);
    const blocked = deckMode === 'ranked' && !!rankedBlockReason(card);
    return (
      <div className="db-card-actions" onClick={(e) => e.stopPropagation()}>
        <button
          className="db-action-btn db-action-eye"
          title="Voir les détails"
          aria-label="Voir les détails"
          onClick={() => setSelectedCard(card)}
        ><EyeIcon /></button>
        <button
          className="db-action-btn db-action-add"
          title={blocked ? rankedBlockReason(card) : 'Ajouter au deck'}
          aria-label={blocked ? 'Carte non disponible en classé' : 'Ajouter au deck'}
          disabled={blocked}
          onClick={() => addCardToDeck(card)}
        ><PlusIcon /></button>
        <button
          className="db-action-btn db-action-remove"
          title="Retirer du deck"
          aria-label="Retirer du deck"
          disabled={inDeck === 0}
          onClick={() => removeCardFromDeck(card.id)}
        ><MinusIcon /></button>
      </div>
    );
  };

  return (
    <div className="deck-builder-container">
      <div className="deck-builder-header">
        <h1>{deckId ? 'Modifier le Deck' : 'Créer un Nouveau Deck'}</h1>
        <div className="header-actions">
          <button onClick={cancelEditing} className="cancel-button">
            Annuler
          </button>
          <button
            onClick={saveDeck}
            className="save-button"
            disabled={saving}
          >
            {saving ? 'Sauvegarde...' : 'Sauvegarder'}
          </button>
        </div>
      </div>

      {error && <div className="deck-builder-error">{error}</div>}

      <div className="deck-builder-content">
        <div className="deck-info-panel">
          <h2>Informations du Deck</h2>
          <div className="deck-info-form">
            <div className="form-group">
              <label htmlFor="name">Nom du deck</label>
              <input
                type="text"
                id="name"
                name="name"
                value={deckInfo.name}
                onChange={handleDeckInfoChange}
                placeholder="Entrez un nom pour votre deck"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="element">Élément</label>
              <select
                id="element"
                name="element"
                value={deckInfo.element}
                onChange={handleDeckInfoChange}
                disabled={deckCards.length > 0}
              >
                <option value="FIRE">Feu</option>
                <option value="WATER">Eau</option>
                <option value="EARTH">Terre</option>
                <option value="AIR">Vent</option>
                <option value="LIGHT">Lumière</option>
                <option value="DARK">Ténèbres</option>
              </select>
              {deckCards.length > 0 && (
                <p className="element-locked-hint">L'élément ne peut pas être modifié une fois des cartes ajoutées.</p>
              )}
            </div>

            <div className="form-group">
              <label>Mode de jeu</label>
              <div className="db-mode-selector">
                <button
                  type="button"
                  className={`db-mode-btn ${deckMode === 'normal' ? 'active' : ''}`}
                  onClick={() => setDeckMode('normal')}
                  title="Utilisable en casual et PvE, aucune restriction"
                >
                  ⚔️ Normal
                  <small>Casual / PvE</small>
                </button>
                <button
                  type="button"
                  className={`db-mode-btn ${deckMode === 'ranked' ? 'active' : ''}`}
                  onClick={() => setDeckMode('ranked')}
                  title="Duels classés : cartes en vente (escrow) et verrouillées sur un autre compte interdites"
                >
                  🏆 Classé
                  <small>Ranked</small>
                </button>
              </div>
              {deckMode === 'ranked' && (
                <p className="db-mode-hint">
                  En classé, les cartes listées en vente (🛒 halo rouge) et celles déjà utilisées en classé par un autre compte (🔒) ne peuvent pas être ajoutées.
                </p>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                name="description"
                value={deckInfo.description}
                onChange={handleDeckInfoChange}
                placeholder="Décrivez votre deck en quelques mots"
                rows="2"
              ></textarea>
            </div>

            <div className="form-group">
              <label htmlFor="strategy">Stratégie</label>
              <textarea
                id="strategy"
                name="strategy"
                value={deckInfo.strategy}
                onChange={handleDeckInfoChange}
                placeholder="Expliquez la stratégie de jeu de ce deck"
                rows="2"
              ></textarea>
            </div>

            <div className="form-group">
              <label htmlFor="difficulty">Difficulté</label>
              <select
                id="difficulty"
                name="difficulty"
                value={deckInfo.difficulty}
                onChange={handleDeckInfoChange}
              >
                <option value="Débutant">Débutant</option>
                <option value="Intermédiaire">Intermédiaire</option>
                <option value="Avancé">Avancé</option>
              </select>
            </div>
          </div>
          <div className="deck-stats">
            <h3>Statistiques du Deck</h3>
            <div className="stats-grid">
              <div className="stat-item">
                <span className="stat-label">Cartes</span>
                <span className="stat-value">{deckCards.length}/30</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Personnages</span>
                <span className="stat-value">{countCardsByType('CHARACTER')}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Sorts</span>
                <span className="stat-value">{countCardsByType('SPELL')}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Pièges</span>
                <span className="stat-value">{countCardsByType('TRAP')}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Équipements</span>
                <span className="stat-value">{countCardsByType('EQUIPMENT')}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Terrains</span>
                <span className="stat-value">{countCardsByType('TERRAIN')}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Coût moyen</span>
                <span className="stat-value">{calculateAverageCost()}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="deck-builder-main">
          <div className="tabs-container">
            <div className="tabs">
              <button
                className={`tab ${activeTab === 'search' ? 'active' : ''}`}
                onClick={() => setActiveTab('search')}
              >
                Recherche de cartes
              </button>
              <button
                className={`tab ${activeTab === 'deck' ? 'active' : ''}`}
                onClick={() => setActiveTab('deck')}
              >
                Deck ({deckCards.length}/30)
              </button>
            </div>

            <div className="tab-content">
              {/* --- Barres de filtres / affichage / tri : IDENTIQUES à "Mes cartes" --- */}
              <div className="collection-filters">
                <div className="filter-group">
                  <label htmlFor="card-element-filter">Élément</label>
                  <select
                    id="card-element-filter"
                    value={filters.element}
                    onChange={(e) => handleFilterChange('element', e.target.value)}
                  >
                    <option value="all">Tous</option>
                    <option value="FIRE">Feu</option>
                    <option value="WATER">Eau</option>
                    <option value="EARTH">Terre</option>
                    <option value="AIR">Vent</option>
                    <option value="LIGHT">Lumière</option>
                    <option value="DARK">Ténèbres</option>
                    <option value="RAINBOW">Arc-en-Ciel</option>
                  </select>
                </div>

                <div className="filter-group">
                  <label htmlFor="card-type-filter">Type</label>
                  <select
                    id="card-type-filter"
                    value={filters.type}
                    onChange={(e) => handleFilterChange('type', e.target.value)}
                  >
                    <option value="all">Tous</option>
                    <option value="CHARACTER">Personnage</option>
                    <option value="SPELL">Sort</option>
                    <option value="TRAP">Piège</option>
                    <option value="EQUIPMENT">Équipement</option>
                    <option value="TERRAIN">Terrain</option>
                  </select>
                </div>

                <div className="filter-group">
                  <label htmlFor="card-rarity-filter">Rareté</label>
                  <select
                    id="card-rarity-filter"
                    value={filters.rarity}
                    onChange={(e) => handleFilterChange('rarity', e.target.value)}
                  >
                    <option value="all">Toutes</option>
                    <option value="COMMON">Commune (C)</option>
                    <option value="RARE">Rare (R)</option>
                    <option value="EPIC">Épique (E)</option>
                    <option value="LEGENDARY">Légendaire (L)</option>
                    <option value="RAINBOW">Arc-en-Ciel (AC)</option>
                  </select>
                </div>

                <div className="filter-group">
                  <label htmlFor="card-effect-filter">Effet</label>
                  <select
                    id="card-effect-filter"
                    value={filters.effect}
                    onChange={(e) => handleFilterChange('effect', e.target.value)}
                  >
                    <option value="all">Tous</option>
                    <option value="with">Avec effet</option>
                    <option value="without">Sans effet</option>
                  </select>
                </div>

                <div className="filter-group search-group">
                  <label htmlFor="card-search-filter">Recherche</label>
                  <input
                    id="card-search-filter"
                    type="text"
                    placeholder="Nom ou texte"
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                  />
                </div>
              </div>

              <div className="collection-display-bar">
                <div className="display-mode-toggle">
                  <button
                    className={`mode-btn ${displayMode === 'list' ? 'active' : ''}`}
                    onClick={() => setDisplayMode('list')}
                    title="Affichage en liste"
                    aria-label="Affichage en liste"
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><rect x="1" y="2" width="5" height="5" rx="1"/><rect x="1" y="9" width="5" height="5" rx="1"/><rect x="8" y="3" width="7" height="1.5" rx=".75"/><rect x="8" y="6.5" width="7" height="1.5" rx=".75"/><rect x="8" y="10" width="7" height="1.5" rx=".75"/><rect x="8" y="13.5" width="7" height="1.5" rx=".75"/></svg>
                    Liste
                  </button>
                  <button
                    className={`mode-btn ${displayMode === 'grid' ? 'active' : ''}`}
                    onClick={() => setDisplayMode('grid')}
                    title="Affichage en grille"
                    aria-label="Affichage en grille"
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><rect x="1" y="1" width="6" height="6" rx="1"/><rect x="9" y="1" width="6" height="6" rx="1"/><rect x="1" y="9" width="6" height="6" rx="1"/><rect x="9" y="9" width="6" height="6" rx="1"/></svg>
                    Grille
                  </button>
                </div>
                {displayMode === 'grid' && (
                  <div className="grid-columns-picker" title="Nombre de colonnes">
                    <span className="cols-label">Colonnes</span>
                    {[3, 4, 5, 6, 7, 8].map((n) => (
                      <button
                        key={n}
                        className={`col-btn ${gridColumns === n ? 'active' : ''}`}
                        onClick={() => setGridColumns(n)}
                      >{n}</button>
                    ))}
                  </div>
                )}
                <div className="sort-picker">
                  <label htmlFor="deck-sort-select" className="cols-label">Tri</label>
                  <select id="deck-sort-select" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                    <option value="default">Par défaut</option>
                    <option value="acquisition">Acquisition (récentes)</option>
                    <option value="cost-asc">Coût croissant</option>
                    <option value="cost-desc">Coût décroissant</option>
                    <option value="name-asc">Nom A-Z</option>
                    <option value="name-desc">Nom Z-A</option>
                    <option value="atk-desc">Attaque décroissante</option>
                    <option value="def-desc">PV décroissants</option>
                    <option value="rarity-desc">Rareté décroissante</option>
                  </select>
                </div>
                <span className="db-cards-count">
                  {activeTab === 'deck'
                    ? `${deckCards.length} carte(s) — ${filteredCards.length} type(s)`
                    : `${filteredCards.length} carte(s)`}
                </span>
              </div>

              {activeTab === 'deck' && deckCards.length === 0 ? (
                <div className="no-cards-message">
                  <p>Votre deck est vide.</p>
                  <p>Commencez par ajouter des cartes depuis l'onglet « Recherche de cartes ».</p>
                </div>
              ) : filteredCards.length === 0 ? (
                <div className="no-cards-message">
                  <p>Aucune carte ne correspond à ces critères.</p>
                  <button onClick={() => setFilters({
                    element: 'all',
                    type: 'all',
                    rarity: 'all',
                    effect: 'all',
                    search: ''
                  })}>Réinitialiser les filtres</button>
                </div>
              ) : (
                displayMode === 'list' ? (
                <div className="cards-list">
                  {sortCards(filteredCards).map((card, index) => (
                    <div
                      key={`card-${card.id}-${index}`}
                      className={`cl-row element-${card.element.toLowerCase()} rarity-glow-${card.rarity.toLowerCase()}${rankedBlockReason(card) ? ' db-card-blocked' : ''}`}
                      onClick={() => setSelectedCard(card)}
                    >
                      <div className="cl-art">
                        <img
                          src={card.imageUrl || `/api/static/images/elements/${card.element.toLowerCase()}.svg`}
                          alt={card.name}
                          loading="lazy"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = `/api/static/images/card-backs/${card.element.toLowerCase()}-back.svg`;
                          }}
                        />
                        <span className={`cost-crystal element-${card.element.toLowerCase()}`}>{card.cost}</span>
                        {card.deckCount > 0 && (
                          <span className="db-deck-badge" title="Exemplaires dans le deck">×{card.deckCount}</span>
                        )}
                        {rankedBlockReason(card) && (
                          <span
                            className={`db-blocked-badge ${cardStatus[card.id] && cardStatus[card.id].listed ? 'is-listed' : 'is-locked'}`}
                            title={rankedBlockReason(card)}
                          >
                            {cardStatus[card.id] && cardStatus[card.id].listed ? '🛒 En vente' : '🔒 Classé'}
                          </span>
                        )}
                      </div>
                      <div className="cl-body">
                        <div className="cl-head">
                          <h3 className="cl-name">{card.name}</h3>
                          <span className={`cl-rarity rarity-${card.rarity.toLowerCase()}`}>{card.rarity}</span>
                        </div>
                        <div className="cl-tags">
                          <span className={`cl-element element-${card.element.toLowerCase()}`}>{card.element}</span>
                          <span className="cl-type">{card.type}</span>
                          {card.type === 'CHARACTER' && (
                            <span className="cl-stats">
                              <b className="cl-atk">ATK {card.attack ?? '-'}</b>
                              <b className="cl-def">PV {card.defense ?? '-'}</b>
                            </span>
                          )}
                        </div>
                        {(card.description || card.effect) && (
                          <p className="cl-desc">{card.description || card.effect}</p>
                        )}
                      </div>
                      {renderCardActions(card)}
                    </div>
                  ))}
                </div>
                ) : (
                <div
                  className="cards-grid"
                  style={{ gridTemplateColumns: `repeat(${gridColumns}, minmax(0, 1fr))` }}
                >
                  {sortCards(filteredCards).map((card, index) => (
                    <div
                      key={`card-${card.id}-${index}`}
                      className={`card-item element-${card.element.toLowerCase()} rarity-glow-${card.rarity.toLowerCase()}${rankedBlockReason(card) ? ' db-card-blocked' : ''}`}
                      onClick={() => setSelectedCard(card)}
                      title={card.name}
                    >
                      <div className="card-image">
                        <img
                          src={card.imageUrl || `/api/static/images/elements/${card.element.toLowerCase()}.svg`}
                          alt={card.name}
                          loading="lazy"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = `/api/static/images/card-backs/${card.element.toLowerCase()}-back.svg`;
                          }}
                        />
                        <span className={`cost-crystal small element-${card.element.toLowerCase()}`}>{card.cost}</span>
                        {card.deckCount > 0 && (
                          <span className="db-deck-badge" title="Exemplaires dans le deck">×{card.deckCount}</span>
                        )}
                        {rankedBlockReason(card) && (
                          <span
                            className={`db-blocked-badge ${cardStatus[card.id] && cardStatus[card.id].listed ? 'is-listed' : 'is-locked'}`}
                            title={rankedBlockReason(card)}
                          >
                            {cardStatus[card.id] && cardStatus[card.id].listed ? '🛒 En vente' : '🔒 Classé'}
                          </span>
                        )}
                      </div>
                      <div className="card-info">
                        <h3 className="card-name">{card.name}</h3>
                      </div>
                      {renderCardActions(card)}
                    </div>
                  ))}
                </div>
                )
              )}
            </div>
          </div>
        </div>
      </div>

      {selectedCard && (
        <CardModal card={selectedCard} onClose={() => setSelectedCard(null)} />
      )}
    </div>
  );
};

export default DeckBuilder;
